"use client";

import Image from "next/image";
import { LazyMotion, domAnimation, m, useReducedMotion } from "framer-motion";

import type { HomeDictionary } from "@/content/home";
import HeroVideo from "@/components/HeroVideo";

interface HeroProps {
  copy: HomeDictionary["hero"];
}

export default function Hero({ copy }: HeroProps) {
  const shouldReduceMotion = useReducedMotion();
  const fadeIn = (delay = 0) => ({
    initial: shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 28 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.75, delay, ease: "easeOut" as const },
  });

  return (
    <LazyMotion features={domAnimation}>
      <section
        id="hero"
        className="relative min-h-screen w-full overflow-hidden flex items-center pt-[100px] md:pt-[126px] bg-[#0A111F]"
      >
        <m.div
          initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="absolute inset-0 z-0"
        >
          <Image
            src="/video-thumbnail.jpg"
            alt={copy.posterAlt}
            fill
            priority
            sizes="100vw"
            quality={70}
            className="object-cover opacity-70"
          />
          <HeroVideo />
          <div className="absolute inset-0 hero-overlay z-10" />
        </m.div>

        <div className="relative z-20 px-6 sm:px-12 lg:px-16 w-full max-w-[1440px] mx-auto">
          <div className="flex flex-col xl:flex-row items-center xl:items-start gap-10 lg:gap-14">
            <m.div
              {...fadeIn(0.08)}
              className="flex-shrink-0"
            >
              <div className="relative w-40 h-40 sm:w-56 sm:h-56 md:w-64 md:h-64 lg:w-72 lg:h-72 xl:w-[320px] xl:h-[320px]">
                <Image
                  src="/logo.webp"
                  alt={copy.logoAlt}
                  fill
                  priority
                  sizes="(max-width: 640px) 160px, (max-width: 1024px) 256px, 320px"
                  className="object-contain"
                />
              </div>
            </m.div>

            <div className="flex flex-col text-center xl:text-left max-w-full">
              <m.h1
                {...fadeIn(0.18)}
                className="font-montserrat font-black text-[9vw] sm:text-5xl md:text-6xl lg:text-7xl xl:text-[82px] text-white uppercase tracking-tighter leading-[0.9] mb-6"
              >
                {copy.titleTop} <br className="hidden sm:block" />
                <span className="text-terracotta">{copy.titleAccent}</span> <br className="hidden sm:block" />
                {copy.titleBottom}
              </m.h1>

              <m.p
                {...fadeIn(0.32)}
                className="font-inter font-light text-base sm:text-lg md:text-xl lg:text-2xl text-white/95 max-w-2xl mb-8 xl:border-l-2 border-terracotta xl:pl-6 mx-auto xl:mx-0"
              >
                {copy.description}
              </m.p>

              <m.div {...fadeIn(0.46)} className="flex justify-center xl:justify-start">
                <m.a
                  href="#soluciones"
                  whileHover={shouldReduceMotion ? undefined : { y: -2 }}
                  whileTap={shouldReduceMotion ? undefined : { scale: 0.98 }}
                  className="group relative h-14 inline-flex items-center justify-center px-10 font-montserrat font-bold text-[12px] uppercase tracking-[0.2em] text-white bg-terracotta overflow-hidden shadow-lg"
                >
                  <span className="relative z-10">{copy.cta}</span>
                  <span className="absolute inset-0 bg-petroleum origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500 ease-out" />
                </m.a>
              </m.div>
            </div>
          </div>
        </div>
      </section>
    </LazyMotion>
  );
}
